// src/components/EditServiceModal.jsx
import React, { useEffect, useState } from "react";
import { BsX, BsUpload } from "react-icons/bs";
import { useServiceStore, useCategoryStore } from "../../store/serviceStore";
import axiosToken from "../../api/axiosClient";
import { toast } from "react-toastify";
import { FiX } from "react-icons/fi";

const EditServiceModal = ({
  isEditModalOpen,
  closeEditModal,
  selectedService,
}) => {
  const [isLoadingUpdateService, setIsLoadingUpdateService] = useState(false);
  const { loadServices, getServicesGroupByCategorie } = useServiceStore();
  const { allCategory, isLoadingCategory, getCategorie } = useCategoryStore();

  useEffect(() => {
    if (isEditModalOpen) {
      getCategorie();
    }
  }, [isEditModalOpen, getCategorie]);

  const [formData, setFormData] = useState({
    nom: "",
    description: "",
    price: "",
    categoryID: "",
    durationMinutes: "",
    image: null,
  });

  const [imagePreview, setImagePreview] = useState(null);

  // Initialiser le formulaire avec les données du service
  useEffect(() => {
    if (selectedService) {
      console.log({ selectedService });
      setFormData({
        nom: selectedService.nom || "",
        description: selectedService.description || "",
        price: selectedService.price || "",
        categoryID:
          selectedService.category?.id?.toString() ||
          selectedService.categoryID?.toString() ||
          "",
        durationMinutes: selectedService.durationMinutes?.toString() || "",
        image: null,
      });

      // Prévisualisation de l'image existante
      // if (selectedService.imageUrl) {
      //   setImagePreview(selectedService.imageUrl);
      // }
    }
  }, [selectedService]);

  // useEffect(() => {
  //   console.log({ formData });
  // }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier la taille du fichier (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("L'image ne doit pas dépasser 5MB");
        return;
      }

      // Vérifier le type de fichier
      if (!file.type.startsWith("image/")) {
        toast.error("Veuillez sélectionner une image valide");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        image: file,
      }));

      // Prévisualisation de la nouvelle image
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: "",
      description: "",
      price: "",
      categoryID: "",
      durationMinutes: "",
      image: null,
    });
    console.log("resetForm...");
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedService) {
      toast.error("Aucun service sélectionné");
      return;
    }

    // Validation des champs requis
    if (!formData.nom.trim()) {
      toast.error("Le nom du service est requis");
      return;
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error("Le prix doit être supérieur à 0");
      return;
    }

    if (!formData.categoryID) {
      toast.error("Veuillez sélectionner une catégorie");
      return;
    }

    try {
      setIsLoadingUpdateService(true);
      const formDataSending = new FormData();

      // Ajouter les champs au FormData seulement s'ils sont modifiés
      Object.keys(formData).forEach((key) => {
        if (
          formData[key] !== null &&
          formData[key] !== undefined &&
          formData[key] !== ""
        ) {
          formDataSending.append(key, formData[key]);
        }
      });

      // Ajouter un flag pour indiquer que c'est une modification
      formDataSending.append("_method", "PUT");

      const response = await axiosToken.put(
        `/service/${selectedService.id}`,
        formDataSending,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Recharger les données
      await Promise.all([loadServices(), getServicesGroupByCategorie()]);

      toast.success("Service modifié avec succès");
      resetForm();
      closeEditModal();
    } catch (error) {
      console.error("Erreur updateService :", error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Erreur lors de la modification du service.";
      toast.error(errorMessage);
    } finally {
      setIsLoadingUpdateService(false);
    }
  };

  const handleClose = () => {
    resetForm();
    closeEditModal();
  };

  // Vérifier si des modifications ont été apportées
  const hasChanges = () => {
    if (!selectedService) return false;

    return (
      formData.nom !== selectedService.nom ||
      formData.description !== selectedService.description ||
      formData.price !== selectedService.price ||
      formData.categoryID !==
        (selectedService.category?.id?.toString() ||
          selectedService.categoryID?.toString()) ||
      formData.durationMinutes !==
        (selectedService.durationMinutes?.toString() || "") ||
      formData.image !== null
    );
  };

  if (!isEditModalOpen || !selectedService) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-2xl">
        {/* En-tête */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="font-poppins font-semibold text-[18px] text-title">
              Modifier le service
            </h2>
            <p className="font-nunito text-[14px] text-text mt-1">
              ID: {selectedService.id}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 transition-colors"
            disabled={isLoadingUpdateService}
          >
            <BsX size={24} />
          </button>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className=" max-h-[60vh] overflow-y-auto">
            {/* Upload d'image */}
            <div>
              <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                Image du service
              </label>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    id="edit-image-upload"
                    disabled={isLoadingUpdateService}
                  />
                  <label
                    htmlFor="edit-image-upload"
                    className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed cursor-pointer transition-colors ${
                      isLoadingUpdateService
                        ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <BsUpload
                      size={24}
                      className={`mb-2 ${
                        isLoadingUpdateService
                          ? "text-gray-300"
                          : "text-gray-400"
                      }`}
                    />
                    <span
                      className={`text-sm font-nunito text-[14px] ${
                        isLoadingUpdateService ? "text-gray-300" : "text-text"
                      }`}
                    >
                      {imagePreview ? "Changer l'image" : "Ajouter une image"}
                    </span>
                    <span className="text-xs text-gray-400 mt-1 font-nunito text-[14px]">
                      Max. 5MB
                    </span>
                  </label>
                </div>
                {(imagePreview || selectedService.imageUrl) && (
                  <div className="w-32 h-32 relative">
                    <img
                      src={
                        imagePreview
                          ? imagePreview
                          : `${
                              process.env.REACT_APP_ENV === "development"
                                ? `${process.env.REACT_APP_DEV_SERVICE_IMG}/`
                                : `${process.env.REACT_APP_API_SERVICE_IMG}/`
                            }${selectedService.imageUrl}`
                      }
                      alt={formData.nom || "Preview"}
                      className="w-full h-full object-cover"
                    />

                    {!isLoadingUpdateService &&
                      !selectedService.imageUrl &&
                      imagePreview !== selectedService.imageUrl && (
                        <button
                          type="button"
                          onClick={() => {
                            setFormData((prev) => ({ ...prev, image: null }));
                            // Si c'était l'image existante, on garde la preview
                            if (
                              selectedService.imageUrl &&
                              imagePreview === selectedService.imageUrl
                            ) {
                              // On garde l'image existante
                            } else {
                              setImagePreview(selectedService.imageUrl || null);
                            }
                          }}
                          className="absolute  top-0 right-0 bg-red-500 text-white w-6 h-6 flex items-center justify-center text-xs"
                        >
                          <FiX />
                        </button>
                      )}
                  </div>
                )}
              </div>

              {selectedService.imageUrl &&
                imagePreview === selectedService.imageUrl && (
                  <p className="font-nunito text-[14px] text-text text-xs mt-2">
                    Image actuelle du service
                  </p>
                )}
            </div>

            {/* Nom du service */}
            <div>
              <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                Nom du service *
              </label>
              <input
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleInputChange}
                required
                disabled={isLoadingUpdateService}
                className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 disabled:bg-gray-50 disabled:cursor-not-allowed font-nunito text-[14px] text-text"
                placeholder="Entrez le nom du service"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                disabled={isLoadingUpdateService}
                className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 disabled:bg-gray-50 disabled:cursor-not-allowed font-nunito text-[14px] text-text"
                placeholder="Description du service"
              />
            </div>

            {/* Prix et Durée */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                  Prix (F CFA) *
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  disabled={isLoadingUpdateService}
                  className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 disabled:bg-gray-50 disabled:cursor-not-allowed font-nunito text-[14px] text-text"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                  Durée (minutes)
                </label>
                <input
                  type="number"
                  name="durationMinutes"
                  value={formData.durationMinutes}
                  onChange={handleInputChange}
                  min="0"
                  disabled={isLoadingUpdateService}
                  className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 disabled:bg-gray-50 disabled:cursor-not-allowed font-nunito text-[14px] text-text"
                  placeholder="Durée en minutes"
                />
              </div>
            </div>

            {/* Catégorie */}
            <div>
              <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                Catégorie *
              </label>
              {isLoadingCategory ? (
                <div className="flex items-center gap-2 py-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-secondary-500"></div>
                  <span className="text-secondary-500 text-sm font-nunito text-[14px]">
                    Chargement des catégories...
                  </span>
                </div>
              ) : (
                <select
                  name="categoryID"
                  value={formData.categoryID}
                  onChange={handleInputChange}
                  required
                  disabled={isLoadingUpdateService || isLoadingCategory}
                  className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 disabled:bg-gray-50 disabled:cursor-not-allowed font-nunito text-[14px] text-text"
                >
                  <option value="">Sélectionnez une catégorie</option>
                  {allCategory?.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Statut du service */}
            <div>
              <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                Statut actuel
              </label>
              <div
                className={`inline-flex items-center px-3 py-1 font-nunito text-[14px] text-text font-medium ${
                  selectedService.is_active
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {selectedService.is_active ? "🟢 Actif" : "🔴 Inactif"}
              </div>
              <p className="font-nunito text-[14px] text-text text-xs mt-1">
                Le statut peut être modifié via le menu d'actions
              </p>
            </div>
          </div>
          {/* Boutons */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <div className="font-nunito text-[14px] text-text text-sm">
              {hasChanges() && "⚠️ Des modifications non sauvegardées"}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoadingUpdateService}
                className="px-6 py-2 text-gray-600 border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-nunito font-semibold text-[14px]"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isLoadingUpdateService || !hasChanges()}
                className="px-6 py-2 bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-nunito font-semibold text-[14px]"
              >
                {isLoadingUpdateService && (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                )}
                {isLoadingUpdateService
                  ? "Modification en cours..."
                  : "Modifier le service"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditServiceModal;
